import { useEffect, useState } from 'react';

import { ProductDetail } from '@/components/ProductDetail';
import type { OrderAppProductVariantBusinessDetailsFragment } from '@/graphql/orderApp/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useIsMdDown } from '@/hooks/useMedia';
import { useCommonModalProviderContext } from '@/providers/CommonModalProvider';

import { parseSaleorProductSku } from '../../helpers';
import type { CardProps } from '../../types';
import { Cta } from '../CTA';

import * as S from '../CardContent/CardContent.styles';

const getVariant = (productData: any) => {
  if (!productData.variant) return '';
  return productData.variant[0]?.node;
};
const getBusinessVariant = (productData: any) => {
  if (!productData.businessVariant)
    return {} as OrderAppProductVariantBusinessDetailsFragment;
  return productData
    .businessVariant[0] as OrderAppProductVariantBusinessDetailsFragment;
};

export const CardCTA = (
  props: CardProps & {
    isCountDown?: boolean | null;
  },
) => {
  const {
    isCountDown,
    mediaWrapperType,
    onClickCta,
    openUrl2InNewTab,
    openUrlInNewTab,
    saleorProductSku,
    textAlignment,
    type,
    url,
    url1EventType,
    url2,
    url2EventType,
    url2Text,
    url2Trigger,
    url2Type,
    urlDirection,
    urlPosition,
    urlText,
    urlTrigger,
    urlType,
  } = props;

  const productData = parseSaleorProductSku(saleorProductSku);
  const variant = getVariant(productData);
  const businessVariant = getBusinessVariant(productData);
  const hasCta = type && (urlText || url2Text);

  const { openNotifyMe, setContactUsModalStatus } =
    useCommonModalProviderContext();
  const {
    lineAdd,
    snapshot: { isProcessing },
  } = useCheckout();
  const [resolvedUrlDirection, setResolvedUrlDirection] =
    useState(urlDirection);
  const isMobileView = useIsMdDown();
  useEffect(() => {
    if (isMobileView) {
      setResolvedUrlDirection('column');
    } else {
      setResolvedUrlDirection(urlDirection);
    }
  }, [isMobileView, urlDirection]);
  const handleAddToCart = async () => {
    if (
      businessVariant?.variantCTA === 'ORDER' ||
      businessVariant?.variantCTA === 'PRE_ORDER'
    ) {
      await lineAdd(variant.id, businessVariant?.variantCTA);
    } else if (businessVariant?.variantCTA === 'NOTIFY') {
      openNotifyMe({ sku: businessVariant?.sku });
    } else {
      setContactUsModalStatus(true, businessVariant?.sku);
    }
  };

  const displayProductCTA: boolean =
    !!businessVariant?.sku &&
    businessVariant.sku !== 'SRVWEXTN' && // The Warranty Extension product
    businessVariant.sku !== 'SRVARCHEXTRAC'; // The Extract from the Archives product

  return (
    <>
      {displayProductCTA ? (
        <S.CTAWrappersContainer
          $isCountDown={isCountDown}
          textAlignment={textAlignment ?? 'center'}
          type={type}
          urlDirection={resolvedUrlDirection ?? undefined}
          urlPosition={urlPosition ?? undefined}
        >
          <ProductDetail.CTA
            cta={businessVariant?.variantCTA}
            data-testid='ProductDetailCTA'
            isProcessing={isProcessing}
            loading={!businessVariant}
            onClick={handleAddToCart}
          />
        </S.CTAWrappersContainer>
      ) : (
        hasCta && (
          <S.CTAWrappersContainer
            $isCountDown={isCountDown}
            $mediaWrapperType={mediaWrapperType}
            textAlignment={textAlignment ?? 'center'}
            type={type}
            urlDirection={resolvedUrlDirection ?? undefined}
            urlPosition={urlPosition ?? undefined}
          >
            {urlText && (
              <S.CTAWrapper
                $hasSecondCTA={!!url2Text}
                $urlDirection={resolvedUrlDirection ?? undefined}
                data-testid='BaseCardTypeCTA1'
              >
                <Cta
                  fluid
                  onClickCta={onClickCta}
                  openUrlInNewTab={openUrlInNewTab}
                  url={url}
                  urlEventType={url1EventType as string}
                  urlText={urlText}
                  urlTrigger={urlTrigger}
                  urlType={urlType}
                />
              </S.CTAWrapper>
            )}
            {url2Text && (
              <S.CTAWrapper
                $hasSecondCTA={!!url2Text}
                $urlDirection={resolvedUrlDirection ?? undefined}
                data-testid='BaseCardTypeCTA2'
              >
                <Cta
                  fluid
                  onClickCta={onClickCta}
                  openUrlInNewTab={openUrl2InNewTab}
                  url={url2}
                  urlEventType={url2EventType as string}
                  urlText={url2Text}
                  urlTrigger={url2Trigger}
                  urlType={url2Type}
                />
              </S.CTAWrapper>
            )}
          </S.CTAWrappersContainer>
        )
      )}
    </>
  );
};
